import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { ApiError } from '../api/client'
import { useFriendAction, useMe, usePublicProfile, useSendFriendRequest } from '../api/hooks'
import type {
  FriendshipStatus,
  PublicGameRatings,
  PublicProfile,
  PublicRatingScope,
  PublicRecentPlay,
} from '../api/types'
import { ScreenShell } from '../components/ScreenShell'
import { formatDateShort } from '../datetime'
import {
  BackButton,
  GameThumb,
  MeepleAvatar,
  NumberCircle,
  Pill,
  RatingAxis,
  RatingGaussian,
  RatingSummary,
  ScorePad,
  ScorePadRow,
  SectionHeader,
  TopBar,
  ratingDomain,
} from '../primitives'

export const OtherProfileScreen = () => {
  const { handle } = useParams({ from: '/u/$handle' })
  const navigate = useNavigate()
  const me = useMe()
  const profile = usePublicProfile(handle)

  // If the viewer routes to their own handle, send them to /me instead.
  if (me.data && profile.data && me.data.id === profile.data.user.id) {
    navigate({ to: '/me', replace: true })
    return null
  }
  // Same case if viewer's handle matches before profile loads.
  if (me.data && me.data.handle.toLowerCase() === handle.toLowerCase()) {
    navigate({ to: '/me', replace: true })
    return null
  }

  if (profile.isLoading) {
    return (
      <ScreenShell>
        <div style={{ padding: 16, color: 'var(--ink-3)' }}>Loading…</div>
      </ScreenShell>
    )
  }
  if (profile.error) {
    const status = profile.error instanceof ApiError ? profile.error.status : null
    return (
      <ScreenShell>
        <TopBar left={<BackButton fallback={() => navigate({ to: '/home' })} />} title="Profile" />
        <div style={{ padding: 16, color: 'var(--ink-2)' }}>
          {status === 404 ? `No user "${handle}".` : 'Could not load profile.'}
        </div>
      </ScreenShell>
    )
  }
  if (!profile.data) return null

  const d = profile.data
  return (
    <ScreenShell>
      <TopBar
        left={<BackButton fallback={() => navigate({ to: '/home' })} />}
        title={d.user.displayName}
        sub={`@${d.user.handle}`}
      />

      <Hero profile={d} />

      <FriendshipPanel profile={d} />

      {d.totals.playsTogether > 0 && (
        <div style={{ padding: '6px 16px', fontSize: 12, color: 'var(--ink-3)' }}>
          You've played {d.totals.playsTogether} game{d.totals.playsTogether === 1 ? '' : 's'}{' '}
          together.
        </div>
      )}

      <SharedGroups profile={d} />

      <Ratings profile={d} />

      <RecentPlays profile={d} />
    </ScreenShell>
  )
}

const Hero = ({ profile }: { profile: PublicProfile }) => (
  <div style={{ padding: '18px 16px 6px', display: 'flex', gap: 14, alignItems: 'center' }}>
    <MeepleAvatar
      name={profile.user.displayName}
      hue={profile.user.avatarHue}
      shape={profile.user.avatarShape}
      size={72}
    />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div className="kicker">player</div>
      <div
        style={{
          fontFamily: 'var(--font-serif)',
          fontStyle: 'italic',
          fontSize: 28,
          lineHeight: 1.05,
          color: 'var(--ink)',
        }}
      >
        {profile.user.displayName}
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>@{profile.user.handle}</div>
    </div>
  </div>
)

const FriendshipPanel = ({ profile }: { profile: PublicProfile }) => {
  const { user, friendship } = profile
  const send = useSendFriendRequest()
  const action = useFriendAction()

  const onAdd = () => send.mutate(user.handle)
  const onAccept = () => action.accept.mutate(user.id)
  const onDecline = () => action.decline.mutate(user.id)
  const onUnfriend = () => action.unfriend.mutate(user.id)
  const onBlock = () => action.block.mutate(user.id)
  const onUnblock = () => action.unblock.mutate(user.id)

  return (
    <div
      style={{
        padding: '8px 16px 0',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
      }}
    >
      {friendship === 'accepted' && <Pill color="felt">Friends</Pill>}
      {friendship === 'pending_outgoing' && <Pill color="slate">Request sent</Pill>}
      {friendship === 'pending_incoming' && <Pill color="gilt">Wants to be friends</Pill>}
      {friendship === 'blocked_them' && <Pill color="terra">You blocked them</Pill>}
      {friendship === 'blocked_by_them' && <Pill color="slate">They blocked you</Pill>}

      <div style={{ flex: 1 }} />

      <FriendshipActions
        friendship={friendship}
        onAdd={onAdd}
        onAccept={onAccept}
        onDecline={onDecline}
        onUnfriend={onUnfriend}
        onBlock={onBlock}
        onUnblock={onUnblock}
      />
    </div>
  )
}

const FriendshipActions = ({
  friendship,
  onAdd,
  onAccept,
  onDecline,
  onUnfriend,
  onBlock,
  onUnblock,
}: {
  friendship: FriendshipStatus
  onAdd: () => void
  onAccept: () => void
  onDecline: () => void
  onUnfriend: () => void
  onBlock: () => void
  onUnblock: () => void
}) => {
  switch (friendship) {
    case 'none':
      return (
        <button type="button" className="btn-stamp" onClick={onAdd}>
          + Add friend
        </button>
      )
    case 'pending_outgoing':
      return (
        <button type="button" className="btn-ghost" onClick={onDecline}>
          Cancel
        </button>
      )
    case 'pending_incoming':
      return (
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" className="btn-ghost" onClick={onDecline}>
            Decline
          </button>
          <button type="button" className="btn-stamp" onClick={onAccept}>
            Accept
          </button>
        </div>
      )
    case 'accepted':
      return (
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" className="btn-ghost" onClick={onUnfriend}>
            Unfriend
          </button>
          <button type="button" className="btn-ghost" onClick={onBlock}>
            Block
          </button>
        </div>
      )
    case 'blocked_them':
      return (
        <button type="button" className="btn-ghost" onClick={onUnblock}>
          Unblock
        </button>
      )
    case 'blocked_by_them':
      return null
  }
}

const SharedGroups = ({ profile }: { profile: PublicProfile }) => {
  if (profile.sharedGroups.length === 0) {
    return (
      <div style={{ padding: '14px 16px' }}>
        <div className="felt-card" style={{ padding: 14, color: 'var(--ink-2)' }}>
          <div style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic', fontSize: 18 }}>
            No shared groups.
          </div>
          <div style={{ marginTop: 4, fontSize: 13 }}>
            Per-group ratings and plays only show when you're in the same group.
          </div>
        </div>
      </div>
    )
  }
  return (
    <>
      <SectionHeader kicker="shared groups" title={`${profile.sharedGroups.length}`} />
      <div style={{ padding: '0 16px' }}>
        <ScorePad>
          {profile.sharedGroups.map((g) => (
            <ScorePadRow key={g.id}>
              <div style={{ flex: 1 }}>
                <Link
                  to="/group/$id"
                  params={{ id: g.id }}
                  style={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: 'var(--ink)',
                    textDecoration: 'none',
                  }}
                >
                  {g.name}
                </Link>
              </div>
              <span style={{ color: 'var(--ink-3)' }}>→</span>
            </ScorePadRow>
          ))}
        </ScorePad>
      </div>
    </>
  )
}

const Ratings = ({ profile }: { profile: PublicProfile }) => {
  if (profile.ratings.length === 0) return null
  return (
    <>
      <SectionHeader kicker="ratings" title="Per-game" />
      <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {profile.ratings.map((r) => (
          <GameRatingsCard key={r.game.id} ratings={r} />
        ))}
      </div>
    </>
  )
}

const GameRatingsCard = ({ ratings }: { ratings: PublicGameRatings }) => (
  <div
    className="felt-card"
    style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <Link
        to="/group/$id/game/$gameId"
        params={{ id: ratings.perGroup[0]?.groupId ?? '', gameId: ratings.game.id }}
        style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}
      >
        <GameThumb label={ratings.game.name} imageUrl={ratings.game.imageUrl} size={42} />
        <div
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
            fontSize: 16,
            color: 'var(--ink)',
          }}
        >
          {ratings.game.name}
        </div>
      </Link>
    </div>
    {ratings.perGroup.map((s) => (
      <RatingScopeRow key={s.groupId} scope={s} />
    ))}
  </div>
)

const RatingScopeRow = ({ scope }: { scope: PublicRatingScope }) => {
  const trend = scope.history.map((h) => h.mu)
  const lastRawDelta = trend.length >= 2 ? trend[trend.length - 1]! - trend[trend.length - 2]! : 0
  const domain = ratingDomain(scope.mu, scope.sigma)
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        paddingTop: 8,
        borderTop: '1.5px dashed var(--paper-edge)',
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 13 }}>{scope.groupName}</div>
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 10 }}>
        <RatingSummary
          tier={scope.tier}
          rawMu={scope.mu}
          playCount={scope.playCount}
          rawDelta={lastRawDelta}
        />
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <RatingGaussian
            mu={scope.mu}
            sigma={scope.sigma}
            domain={domain}
            width={260}
            height={56}
          />
          <RatingAxis xMin={domain[0]} xMax={domain[1]} width={260} />
        </div>
      </div>
    </div>
  )
}

const RecentPlays = ({ profile }: { profile: PublicProfile }) => {
  if (profile.recentPlays.length === 0) return null
  return (
    <>
      <SectionHeader
        kicker="recent"
        title={`${profile.recentPlays.length} plays in shared groups`}
      />
      <div style={{ padding: '0 16px' }}>
        <ScorePad>
          {profile.recentPlays.map((p) => (
            <RecentPlayRow key={p.playId} play={p} />
          ))}
        </ScorePad>
      </div>
    </>
  )
}

const RecentPlayRow = ({ play }: { play: PublicRecentPlay }) => (
  <ScorePadRow>
    <NumberCircle n={play.rank} winner={play.rank === 1} size={22} />
    <GameThumb label={play.game.name} imageUrl={play.game.imageUrl} size={32} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <Link
        to="/play/$id"
        params={{ id: play.playId }}
        style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}
      >
        {play.game.name}
      </Link>
      <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
        {play.groupName} · {formatDateShort(play.playedAt)}
      </div>
    </div>
    <span style={{ color: 'var(--ink-3)' }}>→</span>
  </ScorePadRow>
)
